/**
 * Hovedmenyens faner og hvilken fane som er aktiv (#153, MAPPING.md §4).
 *
 * Ingen URL flyttes: en fane er en gruppe eksisterende ruter, og den aktive
 * fanen avgjøres av stien. Slik lander hver push-lenke og hvert bokmerke med
 * riktig fane tent, uten at rutene må vite om menyen.
 *
 *   Barn og tenåringer: Hjem · Oppgaver · Premier · Plan
 *   Voksne:             Hjem · Godkjenn · Familie · Plan
 *
 * Assistenten (lime knapp) og Meg (avataren i toppen) er ikke faner.
 */
import { matchPath } from 'react-router';
import type { IconName } from '../../components/primitives/Icon';

export type NavTabKey = 'home' | 'chores' | 'rewards' | 'plan' | 'approvals' | 'family';

export interface NavTab {
  key: NavTabKey;
  /** Hvor fanen lenker. */
  to: string;
  i18nKey: string;
  icon: IconName;
  /** Rutemønstrene som tenner fanen. Søkestrengen telles ikke med. */
  activeOn: readonly string[];
}

const HOME: NavTab = {
  key: 'home',
  to: '/',
  i18nKey: 'nav.home',
  icon: 'NavHome',
  activeOn: ['/', '/i-dag', '/familie'],
};

const PLAN: NavTab = {
  key: 'plan',
  to: '/kalender',
  i18nKey: 'nav.plan',
  icon: 'NavPlan',
  activeOn: ['/kalender', '/meny', '/handleliste'],
};

const CHILD_TABS: readonly NavTab[] = [
  HOME,
  {
    key: 'chores',
    to: '/oppgavebunken',
    i18nKey: 'nav.chores',
    icon: 'NavTasks',
    // /oppgaver er videresendingen fra push ved godkjent/avvist oppgave.
    activeOn: ['/oppgavebunken', '/oppgaver', '/lekser'],
  },
  {
    key: 'rewards',
    to: '/belonninger',
    i18nKey: 'nav.rewards',
    icon: 'NavRewards',
    activeOn: [
      '/belonninger',
      '/butikk',
      '/utmerkelser',
      '/leaderboard',
      '/poeng',
      '/:memberId/poeng',
      '/:memberId/utmerkelser',
    ],
  },
  PLAN,
];

const ADULT_TABS: readonly NavTab[] = [
  HOME,
  {
    key: 'approvals',
    to: '/godkjenninger',
    i18nKey: 'nav.approvals',
    icon: 'NavApprove',
    activeOn: ['/godkjenninger'],
  },
  {
    key: 'family',
    to: '/foreldre',
    i18nKey: 'nav.family',
    icon: 'NavFamily',
    activeOn: ['/foreldre', '/settings/admin'],
  },
  PLAN,
];

export function navTabsFor(isAdult: boolean): readonly NavTab[] {
  return isAdult ? ADULT_TABS : CHILD_TABS;
}

/**
 * Fanen som skal være aktiv på `pathname`, eller `null` når ingen er det
 * (Meg, assistenten, eller en side som hører til den andre rollens meny).
 */
export function activeNavTab(pathname: string, isAdult: boolean): NavTabKey | null {
  for (const tab of navTabsFor(isAdult)) {
    if (tab.activeOn.some((path) => matchPath({ path, end: true }, pathname) !== null)) {
      return tab.key;
    }
  }
  return null;
}

/** Ruter der assistentknappen ikke vises — den lenker dit. */
export function showsAssistantFab(pathname: string): boolean {
  return matchPath({ path: '/assistent', end: true }, pathname) === null;
}

/**
 * Ruter der skjermen tegner sin egen topp med avataren, så skallet ikke skal
 * legge sin over (#154). Hjem har hilsen, dato og streak ved siden av
 * avataren. Oppgaver har avataren i datopanelet, og Lekser bruker ScreenHeader
 * med tilbakeknapp (#154, steg 4b). Godkjenn har avataren foran tittelen og
 * telleren (#155, steg 5a). Belønninger har «Premier» i toppen og
 * lommebokkortet over underfanene (#156, steg 6a). Butikk, Merker og Tavle
 * har saldopillen i toppen (#156, steg 6b–6d). Lommebok har tilbakeknapp og
 * Butikk-knapp, også når en voksen ser et annet medlems lommebok (#156, steg
 * 6e). Kalender har «Plan», ny hendelse og avataren i datopanelet (#157, steg
 * 7a), og Middag og Handle har «Plan» og avataren i sitt (#157, steg 7c og
 * 7d). Assistenten har tilbakeknapp, stjernen og ⋯ i sin (#158, steg 8a).
 * Familie (/foreldre) har «Familie» og underfanene Barna · Oppgavemaler ·
 * System under (#159, steg 9a).
 * Skjermene kommer på listen etter hvert som de tar i bruk sin egen
 * ScreenHeader.
 */
const OWN_HEADER_ROUTES = [
  '/',
  '/oppgavebunken',
  '/lekser',
  '/godkjenninger',
  '/belonninger',
  '/butikk',
  '/utmerkelser',
  '/leaderboard',
  '/poeng',
  '/:memberId/poeng',
  '/kalender',
  '/meny',
  '/handleliste',
  '/assistent',
  '/foreldre',
  // #159 (steg 9c): Admin-panelet har ScreenHeader «Administrasjon».
  '/settings/admin',
  // #159 (steg 9d): Meg har ScreenHeader «Meg» med tilbakeknapp.
  '/settings',
  // AI-21 (#193): «Hva Stell husker» har ScreenHeader med tilbake til Meg.
  '/settings/hukommelse',
] as const;

export function drawsOwnHeader(pathname: string): boolean {
  return OWN_HEADER_ROUTES.some((path) => matchPath({ path, end: true }, pathname) !== null);
}
